import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronRight, Building2 } from "lucide-react";
import { useMspOnboardingSummary } from "@/hooks/use-api";
import type { MspTenant, MspAlertSummary } from "@/types/api";

// #787: distinguish lifecycle statuses on the card dot instead of treating
// everything non-active as a generic yellow. Unknown statuses fall back to gray.
const STATUS_DOT: Record<string, string> = {
  active: "bg-green-500",
  suspended: "bg-yellow-500",
  decommissioned: "bg-red-500",
};

interface TenantOverviewCardsProps {
  tenants: MspTenant[];
  alertSummary: MspAlertSummary[];
  isLoading: boolean;
  onSelectTenant: (tenantId: string) => void;
}

export function TenantOverviewCards({
  tenants,
  alertSummary,
  isLoading,
  onSelectTenant,
}: TenantOverviewCardsProps) {
  const onboardingQuery = useMspOnboardingSummary();
  const progressMap = new Map(
    (onboardingQuery.data?.tenants ?? []).map((t) => [t.tenant_id, t]),
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="mt-2 h-4 w-20" />
              <Skeleton className="mt-3 h-8 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (tenants.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
        <div className="rounded-full bg-muted p-3">
          <Building2 className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="mt-4 text-sm font-semibold">No child tenants</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Click &quot;Add Client&quot; above to create your first tenant
        </p>
      </div>
    );
  }

  const summaryMap = new Map(
    alertSummary.map((s) => [s.tenant_id, s]),
  );

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {tenants.map((tenant) => {
        const summary = summaryMap.get(tenant.id);
        const totalAlerts =
          (summary?.critical ?? 0) +
          (summary?.high ?? 0) +
          (summary?.medium ?? 0) +
          (summary?.low ?? 0);

        return (
          <Card key={tenant.id} className="transition-colors hover:border-primary/50">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium">{tenant.name}</h3>
                  <div className="mt-1 flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {tenant.plan}
                    </Badge>
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${STATUS_DOT[tenant.status] ?? "bg-gray-400"}`}
                    />
                    <span className="text-xs text-muted-foreground capitalize">
                      {tenant.status}
                    </span>
                  </div>
                </div>
              </div>

              {summary && totalAlerts > 0 && (
                <div className="mt-3 flex gap-2 text-xs">
                  {summary.critical > 0 && (
                    <Badge variant="critical">C:{summary.critical}</Badge>
                  )}
                  {summary.high > 0 && (
                    <Badge variant="high">H:{summary.high}</Badge>
                  )}
                  {summary.medium > 0 && (
                    <Badge variant="medium">M:{summary.medium}</Badge>
                  )}
                  {summary.low > 0 && (
                    <Badge variant="low">L:{summary.low}</Badge>
                  )}
                </div>
              )}

              {(() => {
                const progress = progressMap.get(tenant.id);
                if (!progress || progress.completed === progress.total) return null;
                return (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Setup</span>
                      <span>{progress.progress}</span>
                    </div>
                    <div className="mt-1 h-1.5 w-full rounded-full bg-muted">
                      <div
                        className="h-1.5 rounded-full bg-primary transition-all"
                        style={{
                          width: `${(progress.completed / progress.total) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                );
              })()}

              <Button
                variant="ghost"
                size="sm"
                className="mt-3 w-full justify-between"
                onClick={() => onSelectTenant(tenant.id)}
              >
                View Details
                <ChevronRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
